/* ============================================================
   Kiosk.jsx — the user-facing POS: kassa, grillen, noteringar,
   sålt idag och guiden.
   ============================================================ */

import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { Brand, Thumb, Stepper, Modal, PaymentTag, SaleItems, LOGO } from '../components/ui.jsx';
import { useStore, useAppMeta, actions, demo, syncIfChanged } from '../store/store.js';
import {
  isoWeek, stockFor, priceFor, discountFor, teamForWeek, grillActive, timeAgo, kr,
  freebies, isFreeSale, rangeFor, revenueBetween, productTallies,
} from '../store/selectors.js';
import { KioskGrill } from './Grill.jsx';
import { WasteDialog } from './Waste.jsx';
import { DemoPinDialog, DemoButton, DemoBar } from './Demo.jsx';
import { APP_VERSION } from '../version.js';

/* Hur ofta kiosken kollar om admin ändrat något. */
const SYNC_EVERY_MS = 10 * 1000;

export function KioskApp({ onExit, initial = 'kassa' }) {
  const state = useStore();
  const meta = useAppMeta();
  const [tab, setTab] = useState(initial);
  const [waste, setWaste] = useState(null); // 'menu' | 'logout'
  const [demoPin, setDemoPin] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  /* Sparar admin nya priser, varor eller lagersaldon syns de här inom några
     sekunder — kiosken frågar efter en revisionsstämpel och hämtar om när
     den ändrats. Pausar när fliken inte syns; fokus kollar direkt igen. */
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') syncIfChanged(); };
    const timer = setInterval(tick, SYNC_EVERY_MS);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
    };
  }, []);
  /* Kansliet-knappen finns bara när admin fyllt i något under Inställningar. */
  const contact = state.settings || {};
  const hasContact = !!(contact.contactName || contact.contactPhone || contact.contactEmail || contact.contactNote);
  /* Har någon redan registrerat svinn idag slipper laget frågan vid utloggning —
     dagsbaserat så det överlever att appen laddas om mitt under passet. Övat
     svinn räknas inte: hellre en fråga för mycket än ett pass som glider
     förbi för att någon registrerade svinnet på låtsas. */
  const today = new Date().toDateString();
  const wasteLogged = !meta.demo && state.waste.some(w => new Date(w.ts).toDateString() === today);

  /* Grillen finns bara de dagar det grillas (vecka och dagar sätts på schemat av admin). */
  const grillOn = grillActive(state);
  const tabs = [
    { id: 'kassa', label: 'Kassa', icon: 'cart' },
    ...(grillOn ? [{ id: 'grill', label: 'Grillen', icon: 'bag' }] : []),
    { id: 'noter', label: 'Noteringar', icon: 'note' },
    { id: 'salt', label: 'Sålt idag', icon: 'receipt' },
    { id: 'guide', label: 'Så funkar kiosken', icon: 'book' },
  ];
  /* Om grillfliken var öppen när veckan bytte, ramla tillbaka till kassan. */
  const activeTab = tab === 'grill' && !grillOn ? 'kassa' : tab;

  const team = teamForWeek(state);
  const week = isoWeek(new Date());
  const openNotes = state.notes.filter(n => !n.done).length;
  const g = state.grill || { prepared: 0, sold: 0 };
  const toGrill = Math.max(0, g.prepared - g.sold);

  return (
    <div className="stage">
      {meta.demo && <DemoBar onStop={() => demo.stop()} />}

      {/* top bar */}
      <div className="kiosk-topbar">
        <div>
          <Brand kind="kiosk" size={44} />
          <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.4px', paddingLeft: 57, marginTop: 1 }}>v {APP_VERSION}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, background: '#eaf2ec', border: '1.5px solid #d3e6d8', borderRadius: 999, padding: '8px 8px 8px 18px' }}>
          <div style={{ lineHeight: 1.15, minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '.6px', color: 'var(--muted)', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Veckans kioskansvar</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, color: 'var(--green-deep)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{team || 'Inget lag inlagt'}</div>
          </div>
          <div className="center" style={{ background: 'var(--green)', color: '#fff', borderRadius: 999, padding: '8px 14px', fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16 }}>v.{week}</div>
        </div>
        <div className="kiosk-topbar-actions">
          {(state.demoPinSet || meta.demo) && (
            <DemoButton active={meta.demo} onStart={() => setDemoPin(true)} onStop={() => demo.stop()} />
          )}
          {hasContact && (
            <button className="btn btn-ghost" onClick={() => setContactOpen(true)} title="Kontaktuppgifter till kansliet"><Icon name="phone" size={18} /> Kansliet</button>
          )}
          <button className="btn btn-ghost" onClick={() => setWaste('menu')}><Icon name="trash" size={18} /> Svinn</button>
          <button className="btn btn-ghost" title="Logga ut" onClick={() => (wasteLogged ? onExit() : setWaste('logout'))}><Icon name="back" size={18} /> Logga ut</button>
        </div>
      </div>

      {/* tab bar */}
      <div className="scroll-x" style={{ display: 'flex', gap: 8, padding: '12px 22px 0', background: 'var(--card)', flexShrink: 0, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '12px 20px', border: 'none', cursor: 'pointer', flexShrink: 0,
              borderRadius: '14px 14px 0 0', fontWeight: 800, fontSize: 15.5, position: 'relative',
              background: activeTab === t.id ? 'var(--cream)' : 'transparent',
              color: activeTab === t.id ? 'var(--green-deep)' : 'var(--muted)',
            }}>
            <Icon name={t.icon} size={19} />{t.label}
            {t.id === 'noter' && openNotes > 0 && <span className="qty-badge">{openNotes}</span>}
            {t.id === 'grill' && toGrill > 0 && <span className="qty-badge">{toGrill}</span>}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, minHeight: 0, background: 'var(--cream)' }}>
        {activeTab === 'kassa' && <KioskRegister />}
        {activeTab === 'grill' && <KioskGrill />}
        {activeTab === 'noter' && <KioskNotes />}
        {activeTab === 'salt' && <KioskSalesToday />}
        {activeTab === 'guide' && <KioskGuide team={team} week={week} />}
      </div>

      {waste && (
        <WasteDialog atLogout={waste === 'logout'} onClose={() => setWaste(null)}
          onDone={() => { const wasLogout = waste === 'logout'; setWaste(null); if (wasLogout) onExit(); }} />
      )}

      {demoPin && <DemoPinDialog onClose={() => setDemoPin(false)} />}
      {contactOpen && <ContactDialog contact={contact} onClose={() => setContactOpen(false)} />}
    </div>
  );
}

/* ================= KANSLIET ================= */

/* Kontaktuppgifterna admin lagt in — numret och adressen är klickbara så
   iPaden ringer eller öppnar mejlen direkt. */
function ContactDialog({ contact, onClose }) {
  const phone = (contact.contactPhone || '').trim();
  const email = (contact.contactEmail || '').trim();
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#fbf6ec', borderRadius: 16, textDecoration: 'none', color: 'var(--ink)' };
  const iconStyle = { width: 46, height: 46, borderRadius: 14, background: 'var(--green)', color: '#fff', flexShrink: 0 };
  return (
    <Modal title="📞 Kontakta kansliet" onClose={onClose}
      footer={<><div style={{ flex: 1 }} /><button className="btn btn-primary btn-lg" onClick={onClose}>Stäng</button></>}>
      {contact.contactName && (
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22, color: 'var(--green-deep)', marginBottom: 6 }}>{contact.contactName}</div>
      )}
      <p style={{ margin: '0 0 16px', fontSize: 14.5, fontWeight: 600, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
        Strular något under passet, saknas växel eller tar en vara slut — hör av er.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {phone && (
          <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} style={rowStyle}>
            <span className="center" style={iconStyle}><Icon name="phone" size={22} /></span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Ring</span>
              <span style={{ display: 'block', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22 }}>{phone}</span>
            </span>
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} style={rowStyle}>
            <span className="center" style={{ ...iconStyle, background: 'var(--orange)' }}><Icon name="mail" size={22} /></span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.5px' }}>Mejla</span>
              <span style={{ display: 'block', fontWeight: 800, fontSize: 17, wordBreak: 'break-all' }}>{email}</span>
            </span>
          </a>
        )}
        {contact.contactNote && (
          <div className="card-flat" style={{ padding: '12px 16px', display: 'flex', gap: 11, alignItems: 'flex-start', borderLeft: '5px solid var(--orange)' }}>
            <Icon name="info" size={19} color="var(--orange-deep)" />
            <div style={{ fontSize: 14.5, lineHeight: 1.5, fontWeight: 600, color: 'var(--ink-soft)', whiteSpace: 'pre-wrap' }}>{contact.contactNote}</div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ================= REGISTER / KASSA ================= */

/* Swish tillåter inte betalningar under 20 kr — kontant går alltid bra. */
const MIN_SWISH = 20;

function KioskRegister() {
  const state = useStore();
  const [cart, setCart] = useState([]);
  const [filter, setFilter] = useState('all');
  const [paid, setPaid] = useState(null);      // {total, count} eller {free, name, emoji, count}
  const [confirm, setConfirm] = useState(null); // 'swish'
  /* Tillvalsdialogen: antal + vilken dricka per enhet. `kind` satt = gratis
     till domare/ledare, annars ett vanligt köp som läggs i kassan. */
  const [pick, setPick] = useState(null);      // {product, qty, kind?: 'domare'|'ledare'}
  const [busy, setBusy] = useState(false);

  /* Admin kan ha ändrat pris eller namn, eller tagit bort en vara, medan
     något ligger i kassan — raderna följer katalogen så summan stämmer med
     vad servern tar betalt. */
  useEffect(() => {
    setCart(c => {
      let changed = false;
      const next = c.flatMap(line => {
        const p = state.products.find(x => x.id === line.id);
        if (!p) { changed = true; return []; }
        const price = priceFor(p, state);
        if (price === line.price && p.name === line.name && p.emoji === line.emoji && p.cost === line.cost) return [line];
        changed = true;
        return [{ ...line, name: p.name, emoji: p.emoji, price, cost: p.cost }];
      });
      return changed ? next : c;
    });
  }, [state.products, state.groups]);

  const stockOf = p => stockFor(state, p);
  const inCart = id => cart.find(c => c.id === id);

  /* Allt i kassan räknat per vara — tillvalen (drickan i målet) är egna enheter. */
  const reservedUnits = () => {
    const m = new Map();
    for (const c of cart) {
      m.set(c.id, (m.get(c.id) || 0) + c.qty);
      for (const id of c.choices || []) if (id) m.set(id, (m.get(id) || 0) + 1);
    }
    return m;
  };

  /* Recipe products share lagervaror, so what is already in the cart counts too. */
  const available = p => {
    const units = reservedUnits();
    if (!p.recipe) return stockOf(p) - (units.get(p.id) || 0);
    return Math.min(...p.recipe.map(([iid, q]) => {
      const ing = (state.ingredients || []).find(i => i.id === iid);
      if (!ing) return 0;
      let reserved = 0;
      for (const [id, n] of units) {
        const cp = state.products.find(x => x.id === id);
        const r = cp && cp.recipe && cp.recipe.find(([id2]) => id2 === iid);
        if (r) reserved += r[1] * n;
      }
      return Math.floor((ing.stock - reserved) / q);
    }));
  };

  /* Lägg `qty` enheter i kassan, med ett valt tillval per enhet (null = inget). */
  function addUnits(p, qty, choices = []) {
    setCart(c => {
      const e = c.find(x => x.id === p.id);
      return e
        ? c.map(x => (x.id === p.id ? { ...x, qty: x.qty + qty, choices: [...(x.choices || []), ...choices] } : x))
        : [...c, { id: p.id, name: p.name, emoji: p.emoji, price: priceFor(p, state), cost: p.cost, qty, choices }];
    });
  }

  function add(p) {
    if (available(p) <= 0) return;
    /* Varor med tillval frågar först vilken dricka som togs. */
    if (p.choiceGroupId) setPick({ product: p, qty: 1 });
    else addUnits(p, 1);
  }

  const setQty = (id, q) => {
    const line = cart.find(x => x.id === id);
    const p = state.products.find(x => x.id === id);
    /* Ett mål till betyder en dricka till att välja — resten sköter dialogen. */
    if (line && p && p.choiceGroupId && q > line.qty) {
      setPick({ product: p, qty: q - line.qty });
      return;
    }
    setCart(c => (q <= 0
      ? c.filter(x => x.id !== id)
      : c.map(x => (x.id === id ? { ...x, qty: q, choices: (x.choices || []).slice(0, q) } : x))));
  };

  const total = cart.reduce((a, c) => a + c.price * c.qty, 0);
  const count = cart.reduce((a, c) => a + c.qty, 0);

  async function pay(payment) {
    if (!cart.length || busy) return;
    /* Under Swish-gränsen? Stanna och fråga — kontant går bra, annars fler varor. */
    if (!payment && total < MIN_SWISH) {
      setConfirm('swish');
      return;
    }
    setConfirm(null);
    setBusy(true);
    try {
      await actions.recordSale(cart, payment);
      setPaid({ total, count });
      setCart([]);
      setTimeout(() => setPaid(null), 2200);
    } catch {
      /* the store already surfaced the message — keep the cart so it can be retried */
    } finally {
      setBusy(false);
    }
  }

  /* Dialogen är klar: vanligt köp läggs i kassan, gratis till domare/ledare
     registreras direkt som ett eget köp till 0 kr — skilt från kundens kassa
     så en korv till domaren aldrig blandas in i någon annans köp. */
  async function confirmPick(qty, choices) {
    const { product: p, kind } = pick;
    if (!kind) {
      addUnits(p, qty, choices);
      setPick(null);
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await actions.recordSale([{ id: p.id, qty, choices }], kind);
      setPaid({ total: 0, count: qty, free: kind, name: p.name, emoji: p.emoji });
      setPick(null);
      setTimeout(() => setPaid(null), 2200);
    } catch {
      /* reported by the store — dialogen står kvar så man kan försöka igen */
    } finally {
      setBusy(false);
    }
  }

  const gifts = freebies(state);
  const hasGifts = gifts.ledare.length + gifts.domare.length > 0;
  const groups = filter === 'all' ? state.groups : filter === 'staff' ? [] : state.groups.filter(g => g.id === filter);
  const showStaff = filter === 'all' || filter === 'staff';
  const grillOn = grillActive(state);
  const onGrill = Math.max(0, (state.grill?.prepared || 0) - (state.grill?.sold || 0));

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      {/* product side */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Chips radbryts så alla grupper syns — i stående läge försvann de
            annars in under kassapanelen. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, padding: '16px 22px 12px', flexShrink: 0 }}>
          <Chip active={filter === 'all'} onClick={() => setFilter('all')} label="Alla" emoji="🧺" />
          {state.groups.map(g => (
            <Chip key={g.id} active={filter === g.id} onClick={() => setFilter(g.id)} label={g.name} emoji={g.emoji} color={g.color} />
          ))}
          {hasGifts && <Chip active={filter === 'staff'} onClick={() => setFilter('staff')} label="Domare & ledare" emoji="🧑‍⚖️" color={STAFF_COLOR} />}
        </div>

        <div className="scroll" style={{ flex: 1, padding: '4px 22px 22px' }}>
          {state.products.length === 0 && (
            <div className="center" style={{ flexDirection: 'column', gap: 12, height: '100%', textAlign: 'center', padding: 30, color: 'var(--muted)' }}>
              <div style={{ fontSize: 52, opacity: .5 }}>🧺</div>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20, color: 'var(--green-deep)' }}>Inga varor i kassan än</div>
              <div style={{ fontWeight: 600, fontSize: 14.5, maxWidth: 340, lineHeight: 1.5 }}>
                Be admin lägga upp sortimentet under <b>Varor &amp; lager</b>, så dyker det upp här.
              </div>
            </div>
          )}
          {groups.map(g => {
            /* Gratisvarorna till domare & ledare har sin egen sektion längre ned. */
            const items = state.products.filter(p => p.groupId === g.id && !p.freeForLeaders && !p.freeForReferees);
            if (!items.length) return null;
            return (
              <div key={g.id} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '4px 2px 12px' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 3, background: g.color }} />
                  <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, color: 'var(--green-deep)', whiteSpace: 'nowrap' }}>{g.name}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 13 }}>
                  {items.map(p => {
                    const avail = available(p);
                    const st = stockOf(p);
                    const untracked = !Number.isFinite(st);   // hembakat & co — inget lagersaldo
                    const out = !untracked && st <= 0;
                    const low = !untracked && st > 0 && st <= state.settings.lowStock;
                    const price = priceFor(p, state);
                    const disc = discountFor(state, p);
                    const onSale = price !== p.price;
                    const n = inCart(p.id)?.qty || 0;
                    return (
                      <button key={p.id} disabled={out || avail <= 0} onClick={() => add(p)}
                        style={{
                          position: 'relative', textAlign: 'left', padding: '14px', borderRadius: 18,
                          cursor: out || avail <= 0 ? 'not-allowed' : 'pointer',
                          background: 'var(--card)', border: '2px solid ' + (n > 0 ? 'var(--green)' : 'var(--line-soft)'),
                          boxShadow: n > 0 ? '0 4px 12px rgba(47,125,73,.15)' : 'var(--shadow-sm)',
                          opacity: out ? 0.55 : 1, transition: 'transform .08s, border-color .15s',
                        }}>
                        {n > 0 && <span className="qty-badge" style={{ position: 'absolute', top: -9, right: -7, boxShadow: 'var(--shadow)' }}>{n}</span>}
                        {onSale && <span style={{ position: 'absolute', top: 10, right: 10, background: 'var(--orange)', color: '#fff', borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 800 }}>{disc > 0 ? `−${disc} %` : 'EXTRAPRIS'}</span>}
                        <Thumb p={p} size={56} radius={14} />
                        <div style={{ fontWeight: 800, fontSize: 15.5, marginTop: 11, lineHeight: 1.2, color: 'var(--ink)' }}>{p.name}</div>
                        {p.choiceGroupId && (
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginTop: 3 }}>+ valfri {groupName(state, p.choiceGroupId).toLowerCase()}</div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8 }}>
                          <span style={{ whiteSpace: 'nowrap' }}>
                            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 19, color: onSale ? 'var(--orange-deep)' : 'var(--green-deep)' }}>{price} kr</span>
                            {onSale && <s style={{ marginLeft: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>{p.price} kr</s>}
                          </span>
                          {untracked
                            ? null
                            : out
                              ? <span className="pill tag-bad" style={{ padding: '3px 9px', fontSize: 11.5 }}>SLUT</span>
                              : <span className="pill" style={{ padding: '3px 9px', fontSize: 11.5, background: low ? '#fbeed8' : '#edf3ee', color: low ? 'var(--orange-deep)' : 'var(--muted)' }}>{st} kvar</span>}
                        </div>
                        {/* Hamburgarmålet visar grillen: varje sålt plockar en burgare därifrån. */}
                        {p.meal && grillOn && (
                          <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800, color: onGrill > 0 ? 'var(--orange-deep)' : 'var(--muted)' }}>
                            🔥 {onGrill} på grillen
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {showStaff && state.products.length > 0 && (
            <StaffSection gifts={gifts} state={state} available={available}
              onPick={(product, kind) => setPick({ product, kind, qty: 1 })} />
          )}
        </div>
      </div>

      {/* cart side */}
      <div style={{ width: 'var(--cart)', flexShrink: 0, background: 'var(--card)', borderLeft: '1.5px solid var(--line)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 20px 14px' }}>
          <Icon name="receipt" size={22} color="var(--green)" />
          <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 19 }}>Kassan</span>
          <div style={{ flex: 1 }} />
          {cart.length > 0 && <button className="btn btn-sm btn-danger" onClick={() => setCart([])}>Töm</button>}
        </div>
        <hr className="divider" />

        <div className="scroll" style={{ flex: 1, padding: '10px 16px' }}>
          {cart.length === 0 && (
            <div className="center" style={{ flexDirection: 'column', gap: 12, height: '100%', color: 'var(--muted)', textAlign: 'center', padding: 20 }}>
              <div style={{ fontSize: 46, opacity: .5 }}>🧾</div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Tryck på varorna<br />kunden köper</div>
            </div>
          )}
          {/* Two lines per rad: namnet får hela bredden, antalet raden under —
              i stående läge fick namn, stepper och summa inte plats sida vid sida. */}
          {cart.map(c => (
            <div key={c.id} style={{ padding: '10px 2px', borderBottom: '1px solid var(--line-soft)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <div className="center" style={{ width: 34, height: 34, borderRadius: 10, background: '#fbf6ec', fontSize: 18, flexShrink: 0 }}>{c.emoji}</div>
                <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                <div style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16, whiteSpace: 'nowrap' }}>{c.price * c.qty} kr</div>
              </div>
              {c.choices && c.choices.some(Boolean) && (
                <div style={{ paddingLeft: 43, marginTop: 5, fontSize: 12.5, color: 'var(--ink-soft)', fontWeight: 600, lineHeight: 1.4 }}>
                  {choiceSummary(c.choices, state.products)}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7, paddingLeft: 43 }}>
                <span style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 600 }}>{c.price} kr/st</span>
                <Stepper small value={c.qty} onChange={q => setQty(c.id, q)} min={0} />
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '16px 20px 20px', borderTop: '1.5px solid var(--line)', background: '#fbf7ee' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
            <span style={{ fontWeight: 800, fontSize: 17, whiteSpace: 'nowrap' }}>Summa{count > 0 ? ` · ${count} st` : ''}</span>
            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 34, color: 'var(--green-deep)', whiteSpace: 'nowrap' }}>{total} kr</span>
          </div>
          {total > 0 && total < MIN_SWISH && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12, padding: '9px 12px', borderRadius: 12,
              background: '#fbeed8', border: '1.5px solid #f0d9ae', color: 'var(--orange-deep)', fontWeight: 700, fontSize: 13, lineHeight: 1.35,
            }}>
              <Icon name="warn" size={18} />
              <span>Under {MIN_SWISH} kr — Swish kräver minst {MIN_SWISH} kr. Kontant går bra.</span>
            </div>
          )}
          <button className="btn btn-primary btn-lg btn-block" disabled={!cart.length || busy} onClick={() => pay()}>
            {busy ? <span className="spinner" /> : <Icon name="check" size={22} />} {busy ? 'Sparar…' : 'Betalt'}
          </button>
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)', marginTop: 9, fontWeight: 600 }}>
            Kunden betalar kontant eller Swish (minst {MIN_SWISH} kr) · tryck Betalt när det är klart
          </div>
        </div>
      </div>

      {confirm === 'swish' && (
        <Modal title={`Under ${MIN_SWISH} kr — påminn kunden`} onClose={() => setConfirm(null)} wide
          footer={<>
            <button className="btn btn-lg btn-danger" disabled={busy} onClick={() => { setCart([]); setConfirm(null); }}>Avbryt köpet</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-lg btn-ghost" disabled={busy} onClick={() => setConfirm(null)}>Lägg till fler varor</button>
            <button className="btn btn-lg btn-primary" disabled={busy} onClick={() => pay('paid')}>
              {busy ? <span className="spinner" /> : <Icon name="check" size={21} />} Betalt ändå
            </button>
          </>}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div className="center" style={{ width: 54, height: 54, borderRadius: 16, background: '#fbeed8', color: 'var(--orange-deep)', flexShrink: 0 }}>
              <Icon name="warn" size={28} />
            </div>
            <div style={{ fontSize: 17, lineHeight: 1.6, fontWeight: 600, color: 'var(--ink-soft)' }}>
              Summan är <b style={{ color: 'var(--green-deep)', fontSize: 19 }}>{total} kr</b> och Swish funkar bara
              från {MIN_SWISH} kr.
              <div style={{ marginTop: 10 }}>
                Säg till kunden: <b style={{ color: 'var(--ink)' }}>&ldquo;Under {MIN_SWISH} kr tar vi helst kontant — eller vill du lägga till något?&rdquo;</b>
              </div>
              <div style={{ marginTop: 10 }}>Det går ändå bra att ta betalt — tryck <b style={{ color: 'var(--ink)' }}>Betalt ändå</b> när det är klart.</div>
            </div>
          </div>
        </Modal>
      )}

      {pick && (
        <ChoiceDialog key={pick.product.id + ':' + (pick.kind || '')} pick={pick} state={state} available={available} busy={busy}
          onClose={() => setPick(null)} onConfirm={confirmPick} />
      )}

      {paid && <PaidOverlay info={paid} />}
    </div>
  );
}

function Chip({ active, onClick, label, emoji, color }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 999, flexShrink: 0,
      border: '1.5px solid ' + (active ? (color || 'var(--green)') : 'var(--line)'), cursor: 'pointer', whiteSpace: 'nowrap',
      background: active ? (color || 'var(--green)') : '#fff', color: active ? '#fff' : 'var(--ink)', fontWeight: 700, fontSize: 15,
      boxShadow: 'var(--shadow-sm)', transition: 'all .12s',
    }}>
      <span style={{ fontSize: 17 }}>{emoji}</span>{label}
    </button>
  );
}

function PaidOverlay({ info }) {
  const k = info.free ? STAFF_KINDS[info.free] : null;
  return (
    <div className="center" style={{ position: 'absolute', inset: 0, zIndex: 70, background: 'rgba(20,46,29,.45)', backdropFilter: 'blur(3px)', animation: 'fade .15s' }}>
      <div className="center" style={{ flexDirection: 'column', gap: 14, background: 'var(--card)', borderRadius: 30, padding: '46px 54px', boxShadow: 'var(--shadow-lg)', animation: 'pop .25s cubic-bezier(.2,.9,.3,1.3)' }}>
        <div className="center" style={{ width: 86, height: 86, borderRadius: 999, background: '#e3f0e7', fontSize: 46 }}>
          {k ? k.emoji : <Icon name="check" size={50} color="var(--green)" stroke={2.6} />}
        </div>
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 30, color: 'var(--green-deep)' }}>
          {k ? 'Utlämnat!' : 'Tack & registrerat!'}
        </div>
        <div style={{ fontSize: 17, color: 'var(--ink-soft)', fontWeight: 700 }}>
          {k
            ? `${info.count > 1 ? `${info.count}× ` : ''}${info.emoji} ${info.name} till ${k.who} · gratis`
            : `${info.count} varor · ${info.total} kr`}
        </div>
      </div>
    </div>
  );
}

/* ================= TILLVAL ================= */

function groupName(state, id) {
  const g = (state.groups || []).find(x => x.id === id);
  return g ? g.name : 'vara';
}

/* "🥤 Cola ×2 · 🧃 Festis" — de valda tillvalen på en kassarad. */
function choiceSummary(ids, products) {
  const counts = new Map();
  for (const id of ids) if (id) counts.set(id, (counts.get(id) || 0) + 1);
  return [...counts].map(([id, n]) => {
    const p = products.find(x => x.id === id);
    return `${p ? p.emoji + ' ' + p.name : 'okänd'}${n > 1 ? ` ×${n}` : ''}`;
  }).join(' · ');
}

/**
 * Antal + vilket tillval som togs per enhet: drickan i Hamburgarmålet, drickan
 * till domarens korv. En rad med "platser", en per enhet — tryck på en dricka
 * så fylls nästa lediga plats; tryck på platsen för att ångra. Samma dialog
 * för vanliga köp (läggs i kassan) och gratis till domare/ledare (bokförs
 * direkt), det är `pick.kind` som avgör.
 */
function ChoiceDialog({ pick, state, available, busy, onClose, onConfirm }) {
  const { product: p, kind } = pick;
  const k = kind ? STAFF_KINDS[kind] : null;
  const [qty, setQtyRaw] = useState(pick.qty || 1);
  const [slots, setSlots] = useState([]);   // per enhet: produkt-id, eller null = utan tillval
  const group = p.choiceGroupId ? state.groups.find(g => g.id === p.choiceGroupId) : null;
  const options = group ? state.products.filter(x => x.groupId === group.id && !x.freeForLeaders && !x.freeForReferees) : [];
  const what = group ? group.name.toLowerCase() : '';
  const max = Math.max(1, available(p));
  const pickedOf = id => slots.filter(s => s === id).length;
  const leftOf = x => available(x) - pickedOf(x.id);   // Infinity för varor utan lagersaldo
  const full = slots.length >= qty;
  const ready = !group || full;

  const setQty = n => {
    const q = Math.min(Math.max(1, n), max);
    setQtyRaw(q);
    setSlots(s => s.slice(0, q));
  };
  const choose = id => setSlots(s => (s.length >= qty ? s : [...s, id]));
  const clearSlot = i => setSlots(s => s.filter((_, j) => j !== i));

  const unit = priceFor(p, state);
  const price = unit * qty;
  const title = k ? `${k.emoji} Gratis till ${k.who}` : `${p.emoji} ${p.name}`;

  return (
    <Modal title={title} onClose={onClose} wide={!!group}
      footer={<>
        <button className="btn btn-ghost" disabled={busy} onClick={onClose}>Avbryt</button>
        <div style={{ flex: 1 }} />
        {group && !full && <span style={{ alignSelf: 'center', fontSize: 13.5, fontWeight: 700, color: 'var(--orange-deep)' }}>Välj {what} till {qty === 1 ? 'målet' : `alla ${qty}`} först</span>}
        <button className={'btn btn-lg ' + (k ? 'btn-orange' : 'btn-primary')} disabled={busy || !ready} onClick={() => onConfirm(qty, slots)}>
          {busy ? <span className="spinner" /> : <Icon name="check" size={21} />}
          {k ? ' Lämna ut gratis' : ` Lägg i kassan · ${price} kr`}
        </button>
      </>}>
      {k && (
        <div style={{ fontSize: 15, lineHeight: 1.55, fontWeight: 600, color: 'var(--ink-soft)', marginBottom: 14 }}>
          {k.sentence} Registreras till <b>0 kr</b> och dras från lagret som vanligt — ingenting hamnar i kundens kassa.
        </div>
      )}

      {/* huvudvaran + antal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#fbf6ec', borderRadius: 16, padding: '14px 16px' }}>
        <Thumb p={p} size={50} radius={13} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
          <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 3 }}>
            {k
              ? <><span style={{ color: 'var(--muted)', textDecoration: 'line-through', marginRight: 8 }}>{price} kr</span><span style={{ color: 'var(--green-deep)' }}>0 kr</span></>
              : <span style={{ color: 'var(--green-deep)' }}>{unit} kr/st{group ? ` · ${what} ingår` : ''}</span>}
          </div>
        </div>
        <Stepper value={qty} min={1} onChange={setQty} />
      </div>
      {Number.isFinite(max) && qty >= max && (
        <div style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--orange-deep)' }}>Fler finns inte i lager just nu.</div>
      )}

      {group && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 15.5, marginBottom: 8 }}>
            Vilken {what} togs?{qty > 1 ? <span style={{ fontWeight: 600, color: 'var(--muted)' }}> — en per {p.meal ? 'mål' : 'st'}</span> : null}
          </div>

          {/* en plats per enhet */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {Array.from({ length: qty }, (_, i) => {
              const id = slots[i];
              const filled = i < slots.length;
              const chosen = id ? state.products.find(x => x.id === id) : null;
              return (
                <button key={i} type="button" onClick={() => filled && clearSlot(i)} disabled={!filled}
                  title={filled ? 'Tryck för att ångra' : undefined}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7, minHeight: 40, padding: '6px 12px', borderRadius: 999,
                    fontWeight: 800, fontSize: 13.5, cursor: filled ? 'pointer' : 'default',
                    border: filled ? '1.5px solid var(--green)' : '1.5px dashed var(--line)',
                    background: filled ? '#e3f0e7' : '#fff', color: filled ? 'var(--green-deep)' : 'var(--muted)',
                  }}>
                  <span style={{ opacity: .7 }}>{i + 1}.</span>
                  {!filled ? `välj ${what}` : chosen ? <>{chosen.emoji} {chosen.name}</> : `utan ${what}`}
                  {filled && <Icon name="x" size={14} />}
                </button>
              );
            })}
          </div>

          {/* alternativen */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 10 }}>
            {options.map(x => {
              const left = leftOf(x);
              const out = Number.isFinite(left) && left <= 0;
              const dis = full || out;
              return (
                <button key={x.id} type="button" disabled={dis} onClick={() => choose(x.id)}
                  style={{
                    textAlign: 'left', padding: '11px 12px', borderRadius: 14, cursor: dis ? 'not-allowed' : 'pointer',
                    background: 'var(--card)', border: '2px solid var(--line-soft)', boxShadow: 'var(--shadow-sm)',
                    opacity: out ? .5 : full ? .7 : 1,
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <Thumb p={x} size={38} radius={11} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{x.name}</div>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: out ? 'var(--bad)' : 'var(--muted)', marginTop: 2 }}>
                        {out ? 'SLUT' : Number.isFinite(left) ? `${left} kvar` : 'ingår'}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            <button type="button" disabled={full} onClick={() => choose(null)}
              style={{
                textAlign: 'left', padding: '11px 12px', borderRadius: 14, cursor: full ? 'not-allowed' : 'pointer',
                background: 'transparent', border: '2px dashed var(--line)', opacity: full ? .6 : 1,
              }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--ink-soft)' }}>Utan {what}</div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', marginTop: 2 }}>tog ingen</div>
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ================= DOMARE & LEDARE ================= */

/* Gratis under match: kaffe till ledarna, korv med bröd till domaren. Admin
   bockar i på varan vem den är gratis till; här lämnas den ut med ett tryck
   och en bekräftelse, helt vid sidan av kundens kassa. */
const STAFF_COLOR = '#2c5a7a';
const STAFF_KINDS = {
  ledare: { emoji: '☕', title: 'Ledare', who: 'ledare', sentence: 'Ledare får kaffe gratis under matcherna.', fg: '#7a5a2c', bg: '#efe6d8' },
  domare: { emoji: '🧑‍⚖️', title: 'Domare', who: 'domaren', sentence: 'Domaren får korv med bröd gratis.', fg: STAFF_COLOR, bg: '#e3ecf4' },
};

function StaffSection({ gifts, state, available, onPick }) {
  const any = gifts.ledare.length + gifts.domare.length > 0;
  return (
    <div style={{ marginBottom: 24, paddingTop: 6, borderTop: '1.5px dashed var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '14px 2px 12px' }}>
        <span style={{ width: 9, height: 9, borderRadius: 3, background: STAFF_COLOR }} />
        <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, color: 'var(--green-deep)', whiteSpace: 'nowrap' }}>Domare &amp; ledare</span>
        <span className="pill" style={{ padding: '3px 10px', fontSize: 11.5, background: '#e3ecf4', color: STAFF_COLOR }}>gratis · 0 kr</span>
      </div>

      {!any && (
        <div className="card-flat" style={{ padding: '14px 18px', display: 'flex', gap: 11, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5, fontWeight: 600, color: 'var(--ink-soft)' }}>
          <Icon name="info" size={19} color={STAFF_COLOR} />
          <span>Inga varor är märkta som gratis än. Admin bockar i <b>Gratis till ledare</b> (kaffe) eller <b>Gratis till domare</b> (korv med bröd) på varan under Varor &amp; lager, så dyker de upp här.</span>
        </div>
      )}

      {Object.entries(STAFF_KINDS).map(([kind, k]) => gifts[kind].length > 0 && (
        <div key={kind} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.5px', textTransform: 'uppercase', color: k.fg, margin: '0 2px 8px' }}>
            {k.emoji} Till {k.who === 'domaren' ? 'domare' : 'ledare'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 13 }}>
            {gifts[kind].map(p => {
              const st = stockFor(state, p);
              const out = Number.isFinite(st) && (st <= 0 || available(p) <= 0);
              return (
                <button key={p.id} disabled={out} onClick={() => onPick(p, kind)}
                  style={{
                    position: 'relative', textAlign: 'left', padding: '14px', borderRadius: 18,
                    cursor: out ? 'not-allowed' : 'pointer',
                    background: 'var(--card)', border: '2px solid ' + k.bg, boxShadow: 'var(--shadow-sm)',
                    opacity: out ? 0.55 : 1, transition: 'transform .08s',
                  }}>
                  <span style={{ position: 'absolute', top: 10, right: 10, background: k.bg, color: k.fg, borderRadius: 999, padding: '3px 9px', fontSize: 11, fontWeight: 800 }}>GRATIS</span>
                  <Thumb p={p} size={56} radius={14} />
                  <div style={{ fontWeight: 800, fontSize: 15.5, marginTop: 11, lineHeight: 1.2, color: 'var(--ink)' }}>{p.name}</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 8 }}>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 19, color: k.fg }}>0 kr</span>
                      <s style={{ marginLeft: 6, fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>{priceFor(p, state)} kr</s>
                    </span>
                    {Number.isFinite(st) && (out
                      ? <span className="pill tag-bad" style={{ padding: '3px 9px', fontSize: 11.5 }}>SLUT</span>
                      : <span className="pill" style={{ padding: '3px 9px', fontSize: 11.5, background: '#edf3ee', color: 'var(--muted)' }}>{st} kvar</span>)}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ================= SÅLT IDAG ================= */

/* Bara att titta på: laget ser vad som slagits in och vad dagen dragit in.
   Rättningar görs av admin under Försäljning — därför inga knappar här. */
const TODAY_ROWS = 80;

function KioskSalesToday() {
  const state = useStore();
  const [from, to] = rangeFor('day');
  const sales = state.sales.filter(s => s.ts >= from && s.ts < to).sort((a, b) => b.ts - a.ts);
  const { rev, profit, count } = revenueBetween(state, from, to);
  const freeCount = sales.filter(isFreeSale).length;
  const top = productTallies(state, from, to).slice(0, 8);
  const label = new Date().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="scroll" style={{ height: '100%', padding: '24px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 'min(880px,100%)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 22, background: 'linear-gradient(120deg,#1d4a2e,#2f7d49)', borderRadius: 26, padding: '22px 28px', color: '#fff', marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 52 }}>🧾</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '1px', opacity: .8 }}>SÅLT IDAG · {label.toUpperCase()}</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 44, lineHeight: 1 }}>{kr(rev)}</div>
            <div style={{ fontSize: 14, opacity: .85, fontWeight: 600, marginTop: 4 }}>
              {count} köp · vinst {kr(profit)}{freeCount > 0 ? ` · ${freeCount} gratis till domare & ledare` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,.12)', borderRadius: 999, padding: '9px 16px', fontSize: 13, fontWeight: 700 }}>
            <Icon name="lock" size={16} /> Bara att titta — admin rättar under Försäljning
          </div>
        </div>

        {sales.length === 0 && (
          <div className="card center" style={{ flexDirection: 'column', gap: 10, padding: '44px 30px', textAlign: 'center', color: 'var(--muted)' }}>
            <div style={{ fontSize: 46, opacity: .5 }}>🧺</div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20, color: 'var(--green-deep)' }}>Inget sålt än idag</div>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>Allt som slås in under Kassa dyker upp här direkt.</div>
          </div>
        )}

        {sales.length > 0 && (
          <div className="grid-main">
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px 12px', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, borderBottom: '1.5px solid var(--line-soft)' }}>Inslaget idag</div>
              {sales.slice(0, TODAY_ROWS).map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderBottom: '1px solid var(--line-soft)' }}>
                  <div style={{ width: 46, flexShrink: 0, fontWeight: 800, fontSize: 14.5, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
                    {new Date(s.ts).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <PaymentTag payment={s.payment} />
                      <SaleItems items={s.items} />
                    </div>
                    {s.by && <div style={{ fontSize: 12, fontWeight: 700, color: s.manual ? 'var(--orange-deep)' : 'var(--muted)', marginTop: 5 }}>{s.manual ? 'Tillagd i efterhand av ' : 'Inslaget av '}{s.by}</div>}
                  </div>
                  <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 18, color: isFreeSale(s) ? 'var(--muted)' : 'var(--green-deep)', flexShrink: 0 }}>{s.total} kr</div>
                </div>
              ))}
              {sales.length > TODAY_ROWS && (
                <div style={{ padding: '12px 20px', textAlign: 'center', color: 'var(--muted)', fontWeight: 600, fontSize: 13 }}>
                  Visar de {TODAY_ROWS} senaste av {sales.length} köp idag.
                </div>
              )}
            </div>

            <div className="card" style={{ padding: '16px 20px', alignSelf: 'start' }}>
              <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 17, marginBottom: 10 }}>Mest sålt idag</div>
              {top.map((t, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < top.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                  <span style={{ fontSize: 20 }}>{t.emoji}</span>
                  <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                  <span style={{ fontWeight: 800, fontFamily: 'var(--display)', fontSize: 16, color: 'var(--green-deep)' }}>{t.qty} st</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 54, textAlign: 'right' }}>{kr(t.rev)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= NOTES ================= */
function KioskNotes() {
  const state = useStore();
  const [text, setText] = useState('');

  async function send() {
    const t = text.trim();
    if (!t) return;
    try {
      await actions.addNote(t);
      setText('');
    } catch { /* reported by the store */ }
  }

  const open = state.notes.filter(n => !n.done);
  const done = state.notes.filter(n => n.done);

  return (
    <div className="scroll" style={{ height: '100%', padding: '24px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 'min(700px,100%)' }}>
        <div className="card" style={{ padding: '22px', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Icon name="note" size={22} color="var(--orange)" />
            <span style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 20 }}>Skriv en notering</span>
          </div>
          <p style={{ margin: '0 0 14px', color: 'var(--ink-soft)', fontSize: 14.5, lineHeight: 1.5 }}>
            Börjar något ta slut eller behöver fyllas på? Skriv det här så ser laget och admin det direkt.
          </p>
          <textarea className="textarea" rows={3} value={text} onChange={e => setText(e.target.value)}
            placeholder="T.ex. Bara 2 kaffe kvar – beställ mer till helgen…" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn btn-orange" disabled={!text.trim()} onClick={send}><Icon name="plus" size={19} /> Lägg till notering</button>
          </div>
        </div>

        {open.length > 0 && (
          <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', margin: '0 4px 10px' }}>
            Att åtgärda · {open.length}
          </div>
        )}
        {open.map(n => (
          <div key={n.id} className="card-flat" style={{ display: 'flex', alignItems: 'flex-start', gap: 13, padding: '15px 16px', marginBottom: 11, borderLeft: '5px solid var(--orange)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15.5, lineHeight: 1.45, fontWeight: 600 }}>{n.text}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 5, fontWeight: 600 }}>{timeAgo(n.ts)} · {n.by}</div>
            </div>
            <button className="btn btn-sm btn-soft" onClick={() => actions.toggleNote(n.id)}><Icon name="check" size={16} /> Klar</button>
            <button className="btn btn-icon btn-ghost" onClick={() => actions.deleteNote(n.id)}><Icon name="trash" size={17} /></button>
          </div>
        ))}

        {done.length > 0 && (
          <>
            <div style={{ fontWeight: 800, fontSize: 14, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', margin: '22px 4px 10px' }}>Avklarat</div>
            {done.map(n => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 8, opacity: .6 }}>
                <Icon name="check" size={18} color="var(--green)" />
                <div style={{ flex: 1, textDecoration: 'line-through', fontSize: 14.5 }}>{n.text}</div>
                <button className="btn btn-icon btn-ghost" onClick={() => actions.deleteNote(n.id)}><Icon name="trash" size={16} /></button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

/* ================= GUIDE ================= */
function KioskGuide({ team, week }) {
  const state = useStore();
  const guide = state.guide || { steps: [], where: [], tips: [], intro: '' };
  const stepIcons = ['cart', 'money', 'check', 'note'];

  return (
    <div className="scroll" style={{ height: '100%', padding: '26px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 'min(820px,100%)' }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', background: 'linear-gradient(120deg,#1d4a2e,#2f7d49)', borderRadius: 26, padding: '26px 28px', color: '#fff', marginBottom: 22 }}>
          <img src={LOGO} style={{ width: 74, height: 74 }} alt="SIF" />
          <div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 28, lineHeight: 1.1 }}>Så funkar kiosken</div>
            <div style={{ fontSize: 15.5, opacity: .9, marginTop: 5, lineHeight: 1.5 }}>{guide.intro}</div>
            <div style={{ fontSize: 15.5, opacity: .9, marginTop: 4, lineHeight: 1.5 }}>
              Det här laget håller i kiosken vecka {week}:
              <b style={{ marginLeft: 6 }}>{team || 'inget lag inlagt'}</b>.
            </div>
          </div>
        </div>

        <div className="grid-2" style={{ gap: 14, marginBottom: 22 }}>
          {guide.steps.map((s, i) => (
            <div key={i} className="card" style={{ padding: '20px', display: 'flex', gap: 15 }}>
              <div className="center" style={{ width: 46, height: 46, borderRadius: 14, background: 'var(--green)', color: '#fff', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 22, flexShrink: 0 }}>{i + 1}</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <Icon name={stepIcons[i % stepIcons.length]} size={19} color="var(--green)" />
                  <span style={{ fontWeight: 800, fontSize: 17 }}>{s.t}</span>
                </div>
                <div style={{ fontSize: 14.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid-2" style={{ gap: 14 }}>
          <div className="card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
              <Icon name="box" size={20} color="var(--orange)" /><span style={{ fontWeight: 800, fontSize: 16.5 }}>Var finns sakerna?</span>
            </div>
            {guide.where.map(([a, b], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: i < guide.where.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                <span style={{ fontWeight: 700, fontSize: 14.5 }}>{a}</span>
                <span style={{ color: 'var(--muted)', fontSize: 14, textAlign: 'right' }}>{b}</span>
              </div>
            ))}
          </div>
          <div className="card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
              <Icon name="star" size={20} color="var(--orange)" /><span style={{ fontWeight: 800, fontSize: 16.5 }}>Bra att veta</span>
            </div>
            {guide.tips.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 0', alignItems: 'flex-start' }}>
                <Icon name="check" size={18} color="var(--green)" />
                <span style={{ fontSize: 14.5, lineHeight: 1.45 }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
